/** Canonical project grain on computed/input metrics. */
export const PROJECT_METRIC_LEVEL = 'PROJECT';
export const ALL_METRIC_LEVELS = 'ALL';
export const AGGREGATE_METRIC_LEVEL = 'AGGREGATE';

const LEVEL_SORT_PRIORITY = [
  'PROJECT',
  'MILESTONE',
  'RELEASE',
  'SPRINT',
  'ASSET_GROUP',
  'ASSET_SUB_GROUP',
  'MODULE',
  'TEAM',
  'OBLIGATION',
  'AGGREGATE',
];

export function formatMetricLevelLabel(type: string | null | undefined): string {
  if (!type) return 'Unknown';
  return type
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

export function isProjectLevelMetric(m: {
  metric_identifier_type?: string | null;
  metric_identifier_name?: string | null;
}): boolean {
  const t = (m.metric_identifier_type || '').toUpperCase();
  if (t === PROJECT_METRIC_LEVEL) return true;
  // Legacy rows: project aggregates had null type and null/empty name
  if (!t) return m.metric_identifier_name == null || m.metric_identifier_name === '';
  return false;
}

/** Stable key for chart series / filters: type + instance name. */
export function metricLevelSeriesKey(
  type: string | null | undefined,
  name: string | null | undefined
): string {
  const t = (type || '').toUpperCase();
  if (t === PROJECT_METRIC_LEVEL || (!t && name == null)) {
    return PROJECT_METRIC_LEVEL;
  }
  if (t === 'AGGREGATE') {
    return `AGGREGATE::${name || 'Aggregate'}`;
  }
  return `${t || 'UNKNOWN'}::${name || ''}`;
}

/** Human label for a type + instance (e.g. "Sprint: July Sprint 1"). */
export function formatMetricLevelInstanceLabel(
  type: string | null | undefined,
  name: string | null | undefined
): string {
  const t = (type || '').toUpperCase();
  if (t === PROJECT_METRIC_LEVEL || (!t && name == null)) return 'Project';
  if (t === 'AGGREGATE') return name || 'Aggregate';
  const level = formatMetricLevelLabel(type);
  return name ? `${level}: ${name}` : level;
}

export function sortMetricLevelTypes(types: string[]): string[] {
  return [...types].sort((a, b) => {
    const ia = LEVEL_SORT_PRIORITY.indexOf(a.toUpperCase());
    const ib = LEVEL_SORT_PRIORITY.indexOf(b.toUpperCase());
    if (ia === -1 && ib === -1) return a.localeCompare(b);
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

export function isAggregateLevelMetric(m: {
  metric_identifier_type?: string | null;
}): boolean {
  return (m.metric_identifier_type || '').toUpperCase() === AGGREGATE_METRIC_LEVEL;
}

/** Match a computed identifier name to a config instance (handles qualified duplicates). */
export function identifierNameMatchesInstance(
  identifierName: string | null | undefined,
  instanceName: string,
  parentName?: string | null
): boolean {
  const ident = (identifierName || '').trim();
  const name = (instanceName || '').trim();
  if (!name) return !ident;
  if (ident === name) return true;
  if (parentName) {
    const parent = parentName.trim();
    return ident === `${parent} / ${name}` || ident === `${parent}|${name}`;
  }
  return ident.endsWith(` / ${name}`) || ident.endsWith(`|${name}`);
}

export interface MetricLevelInstanceRef {
  id: string;
  name: string;
  metric_level_name?: string | null;
  parent_instance_id?: string | null;
  parent_instance_name?: string | null;
}

export interface MetricLevelScope {
  levelType: string;
  instanceId: string | null;
}

export function seriesInMetricLevelScope(
  point: {
    metric_identifier_type?: string | null;
    metric_identifier_name?: string | null;
  },
  scope: MetricLevelScope,
  instances: MetricLevelInstanceRef[] = []
): boolean {
  const level = (scope.levelType || PROJECT_METRIC_LEVEL).toUpperCase();
  if (level === ALL_METRIC_LEVELS) return true;
  if (level === AGGREGATE_METRIC_LEVEL) return isAggregateLevelMetric(point);
  if (level === PROJECT_METRIC_LEVEL) return isProjectLevelMetric(point);
  const type = (point.metric_identifier_type || '').toUpperCase();
  if (type !== level) return false;
  if (!scope.instanceId) return true;
  const inst = instances.find((i) => i.id === scope.instanceId);
  if (!inst) return false;
  const parentName =
    inst.parent_instance_name ||
    instances.find((p) => p.id === inst.parent_instance_id)?.name ||
    null;
  return identifierNameMatchesInstance(point.metric_identifier_name, inst.name, parentName);
}

export function parentInstanceName(
  instance: MetricLevelInstanceRef,
  instances: MetricLevelInstanceRef[]
): string | null {
  if (instance.parent_instance_name) return instance.parent_instance_name;
  if (!instance.parent_instance_id) return null;
  return instances.find((p) => p.id === instance.parent_instance_id)?.name || null;
}

export function instanceOptionLabel(
  instance: MetricLevelInstanceRef,
  instances: MetricLevelInstanceRef[]
): string {
  const parent = parentInstanceName(instance, instances);
  const sameNamePeers = instances.filter(
    (i) =>
      (i.metric_level_name || '').toUpperCase() === (instance.metric_level_name || '').toUpperCase() &&
      (i.name || '').trim().toLowerCase() === (instance.name || '').trim().toLowerCase()
  );
  if (parent && sameNamePeers.length > 1) {
    return `${parent} / ${instance.name}`;
  }
  return instance.name;
}
